/**
 * LHGGeo — one IP-geolocation lookup per page, shared by every feature that
 * needs it (province segmentation, estimated shipping).
 *
 * Before this existed, province detection and estimated-shipping each hit the
 * same Cloudflare worker with their own cache, so a PDP could fire two
 * identical requests. This module keeps the ORIGINAL cache key, shape and TTL
 * (`deuxpardeux:estShip:v2`, same-day + 24h) so nothing is invalidated.
 *
 * Adoption of an early request: snippets/province-head.liquid may already have
 * started the fetch in <head> and parked the raw promise on
 * window.__lhgGeoInFlight. We adopt it instead of issuing a second one.
 *
 * Always resolves — never rejects. `null` means "unknown, carry on".
 */
(function () {
  'use strict';
  if (window.LHGGeo) return;

  var CACHE_KEY = 'deuxpardeux:estShip:v2';
  var CACHE_TTL_MS = 24 * 60 * 60 * 1000;
  var WORKER_TIMEOUT_MS = 6000;

  var inFlight = null;

  function normalize(data) {
    if (!data) return null;
    return {
      postal_code: data.postal_code ? String(data.postal_code) : null,
      country: data.country ? String(data.country) : null,
      region: data.region ? String(data.region) : null,
      city: data.city ? String(data.city) : null,
    };
  }

  function readCache(now) {
    try {
      var raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var v = JSON.parse(raw);
      if (!v || typeof v !== 'object') return null;
      var fetchedAt = new Date(v.fetched_at_iso).getTime();
      if (!isFinite(fetchedAt)) return null;
      if (now.getTime() - fetchedAt >= CACHE_TTL_MS) return null;
      // Same-day only: cutoff/delivery messaging must not survive midnight.
      if (new Date(fetchedAt).toDateString() !== now.toDateString()) return null;
      return {
        postal_code: v.postal_code != null ? v.postal_code : null,
        country: v.country != null ? v.country : null,
        region: v.region != null ? v.region : null,
        city: v.city != null ? v.city : null,
      };
    } catch (e) {
      return null;
    }
  }

  function writeCache(payload, now) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        postal_code: payload.postal_code != null ? payload.postal_code : null,
        country: payload.country != null ? payload.country : null,
        region: payload.region != null ? payload.region : null,
        city: payload.city != null ? payload.city : null,
        fetched_at_iso: now.toISOString(),
      }));
    } catch (e) { /* private mode / storage full — skip */ }
  }

  function fetchWorker(url) {
    var ctrl = new AbortController();
    var timer = setTimeout(function () { ctrl.abort(); }, WORKER_TIMEOUT_MS);
    return fetch(url, { method: 'GET', mode: 'cors', credentials: 'omit', signal: ctrl.signal })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; })
      .then(function (data) { clearTimeout(timer); return data; });
  }

  /**
   * @param {string} url  Cloudflare worker URL
   * @param {Date}   [now]
   * @returns {Promise<{postal_code,country,region,city}|null>}
   */
  function get(url, now) {
    var stamp = now || new Date();

    var cached = readCache(stamp);
    if (cached) return Promise.resolve(cached);

    if (inFlight) return inFlight;

    // Adopt the <head> request if province detection already started one.
    var source = window.__lhgGeoInFlight
      ? Promise.resolve(window.__lhgGeoInFlight).catch(function () { return null; })
      : (url ? fetchWorker(url) : Promise.resolve(null));

    inFlight = source.then(function (data) {
      var res = normalize(data);
      if (res) writeCache(res, stamp);
      return res;
    }).catch(function () {
      return null;
    });

    return inFlight;
  }

  window.LHGGeo = {
    get: get,
    readCache: readCache,
    writeCache: writeCache,
    CACHE_KEY: CACHE_KEY,
  };
})();
